// src/pages/blog/BlogList.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

/* ── Config ─────────────────────────────────────────────────── */
// Use the same env var name as the rest of the shop app (HomePage, etc.)
const API_BASE   = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api";
const MEDIA_BASE = process.env.REACT_APP_MEDIA_BASE   || "http://localhost:3000";

/* ── Logo ───────────────────────────────────────────────────── */
const Logo = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 140 140" fill="none">
    <defs>
      <linearGradient id="shop-bl-ng" x1="30" y1="15" x2="110" y2="125">
        <stop offset="0%" stopColor="#00FFE0" />
        <stop offset="50%" stopColor="#00E5CC" />
        <stop offset="100%" stopColor="#22D97F" />
      </linearGradient>
    </defs>
    <rect width="140" height="140" rx="30" fill="#0F2140" />
    <path d="M70 18L28 37v32c0 26 42 45 42 45s42-19 42-45V37L70 18z" fill="none" stroke="url(#shop-bl-ng)" strokeWidth="4" />
    <circle cx="70" cy="60" r="16" fill="none" stroke="url(#shop-bl-ng)" strokeWidth="2.5" opacity="0.35" />
    <text x="70" y="68" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="800" fontSize="20" fill="url(#shop-bl-ng)">J</text>
  </svg>
);

/* ── Icons ──────────────────────────────────────────────────── */
const Ico = {
  arrowLeft:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  arrowRight: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>,
  search:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>,
  calendar:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></svg>,
  cart:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" /></svg>,
};

/* ── Helpers ────────────────────────────────────────────────── */
function stripHtml(html = "") {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

// Clean a title - strip HTML tags so "<h2>foo</h2>" displays as "foo"
function cleanTitle(raw = "") {
  const cleaned = stripHtml(raw).replace(/\s+/g, " ").trim();
  return cleaned || "Untitled";
}

function getMediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `${MEDIA_BASE}${url}`;
  return `${MEDIA_BASE}/uploads/blogs/${url}`;
}

// Extract first <img src="..."> URL from HTML content. Returns "" if none.
// Handles both src="..." and src='...' with single or double quotes.
function extractFirstImage(html = "") {
  if (!html) return "";
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return match ? match[1] : "";
}

// Resolve the cover image URL for a blog with smart fallback:
//   1. Uploaded coverImage (admin's choice)
//   2. First <img> in content (for imported/scraped posts)
//   3. "" (show placeholder)
function getCoverUrl(blog) {
  if (blog.coverImage) return getMediaUrl(blog.coverImage);
  const firstImg = extractFirstImage(blog.content);
  if (firstImg) return getMediaUrl(firstImg);
  return "";
}

// Format date as "Nov 29, 2025" — clearer than "11/29/2025"
function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric"
    });
  } catch { return ""; }
}

// Return a usable excerpt — uses excerpt field, falls back to content snippet,
// then to "Read more..." so cards never show empty space.
function getExcerpt(blog) {
  const fromExcerpt = stripHtml(blog.excerpt || "").trim();
  if (fromExcerpt) return fromExcerpt.slice(0, 120) + (fromExcerpt.length > 120 ? "..." : "");
  const fromContent = stripHtml(blog.content || "").trim();
  if (fromContent) return fromContent.slice(0, 120) + (fromContent.length > 120 ? "..." : "");
  return "Read more →";
}

/* ── Smart cover image with URL cascade ───────────────────────
 * Tries cover URL first; if it fails (404, CORS, missing file),
 * falls back to first <img> found in content. If none work, shows
 * placeholder. Each card has its own state.
 * ───────────────────────────────────────────────────────────── */
function CoverImage({ blog, alt }) {
  // Build ordered list of URLs to try
  const urls = [];
  if (blog.coverImage) urls.push(getMediaUrl(blog.coverImage));
  const contentImg = extractFirstImage(blog.content);
  if (contentImg) urls.push(getMediaUrl(contentImg));

  const [idx, setIdx] = useState(0);

  // No URLs at all, or exhausted all URLs → placeholder
  if (urls.length === 0 || idx >= urls.length) {
    return <div className="shop-bl-placeholder"><span>📝</span></div>;
  }

  const currentUrl = urls[idx];
  const isVideo = currentUrl.endsWith(".mp4") || currentUrl.endsWith(".mov");

  if (isVideo) {
    return (
      <video
        src={currentUrl}
        className="shop-bl-media"
        controls
        onError={() => setIdx(idx + 1)}
      />
    );
  }

  return (
    <img
      src={currentUrl}
      className="shop-bl-media"
      alt={alt}
      onError={() => setIdx(idx + 1)}  // try next URL, or show placeholder if done
    />
  );
}

/* ══════════════════════════════════════════════════════════════ */
export default function BlogList() {
  const navigate = useNavigate();
  const [blogs, setBlogs]                       = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [searchTerm, setSearchTerm]             = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${API_BASE}/public/blogs`);
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const json = await res.json();

        // Handle multiple response shapes
        const list =
          json.blogs ||
          json.data?.blogs ||
          json.data?.data ||
          (Array.isArray(json.data) ? json.data : null) ||
          (Array.isArray(json) ? json : []);

        setBlogs((list || []).map((b) => ({ ...b, id: b.id || b._id || "" })));
      } catch (err) {
        console.error("Blog load error:", err);
        setError("Failed to load blogs");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const categories = [
    "all",
    ...Array.from(new Set(blogs.map((b) => b.category || "").filter(Boolean))),
  ];

  const filtered = blogs.filter((blog) => {
    const s = searchTerm.toLowerCase();
    const matchSearch =
      cleanTitle(blog.title).toLowerCase().includes(s) ||
      stripHtml(blog.excerpt || "").toLowerCase().includes(s);
    const matchCat = selectedCategory === "all" || blog.category === selectedCategory;
    return matchSearch && matchCat;
  });

  const sorted = [...filtered].sort(
    (a, b) => new Date(b.createdAt || "").getTime() - new Date(a.createdAt || "").getTime()
  );

  return (
    <div className="shop-bl-root">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* Sub-header (secondary nav — not a replacement for main Navbar) */}
      <section className="shop-bl-hero">
        <div className="shop-bl-badge">JepTags Blog</div>
        <h1>Stories, Insights & <span className="grad">Product Guides</span></h1>
        <p>Discover articles about authentication, supply chain, and the products you love.</p>

        {/* Search */}
        <div className="shop-bl-search-wrap">
          <span className="shop-bl-search-ico">{Ico.search}</span>
          <input
            type="text"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </section>

      {/* Categories */}
      {categories.length > 1 && (
        <div className="shop-bl-cats">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`shop-bl-cat ${selectedCategory === cat ? "active" : ""}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat === "all" ? "All" : cat}
            </button>
          ))}
        </div>
      )}

      {/* States */}
      {loading && <div className="shop-bl-msg">Loading...</div>}
      {error && <div className="shop-bl-msg error">{error}</div>}
      {!loading && !error && sorted.length === 0 && (
        <div className="shop-bl-msg">No blog posts found.</div>
      )}

      {/* Grid */}
      {!loading && !error && sorted.length > 0 && (
        <section className="shop-bl-grid-section">
          <div className="shop-bl-grid">
            {sorted.map((blog) => {
              const title = cleanTitle(blog.title);

              return (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="shop-bl-card"
                >
                  <div className="shop-bl-card-img">
                    <CoverImage blog={blog} alt={title} />
                  </div>
                  <div className="shop-bl-card-body">
                    {blog.category && (
                      <span className="shop-bl-card-cat">{blog.category}</span>
                    )}
                    <h2>{title}</h2>
                    <p>{getExcerpt(blog)}</p>
                    <div className="shop-bl-card-footer">
                      {blog.createdAt && (
                        <span className="shop-bl-card-date">
                          <span className="shop-bl-ico-14">{Ico.calendar}</span>
                          {formatDate(blog.createdAt)}
                        </span>
                      )}
                      <span className="shop-bl-card-arrow">{Ico.arrowRight}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
const STYLES = `
.shop-bl-root *,.shop-bl-root *::before,.shop-bl-root *::after{margin:0;padding:0;box-sizing:border-box}
.shop-bl-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;min-height:100vh;
}
.shop-bl-root a{text-decoration:none;color:inherit}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.shop-bl-ico-16 svg{width:16px;height:16px}.shop-bl-ico-14 svg{width:14px;height:14px}.shop-bl-ico-18 svg{width:18px;height:18px}

/* HERO */
.shop-bl-hero{padding:60px 40px 48px;text-align:center;max-width:720px;margin:0 auto}
.shop-bl-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:24px}
.shop-bl-hero h1{font-size:clamp(32px,5vw,52px);font-weight:800;line-height:1.1;letter-spacing:-2px;margin-bottom:16px}
.shop-bl-hero p{font-size:17px;line-height:1.7;color:rgba(224,240,240,0.5);max-width:560px;margin:0 auto 32px}

/* SEARCH */
.shop-bl-search-wrap{position:relative;max-width:560px;margin:0 auto}
.shop-bl-search-ico{position:absolute;left:18px;top:50%;transform:translateY(-50%);width:20px;height:20px;color:rgba(224,240,240,0.25);display:flex}
.shop-bl-search-ico svg{width:20px;height:20px}
.shop-bl-search-wrap input{width:100%;padding:16px 20px 16px 52px;border-radius:14px;border:1.5px solid rgba(0,229,204,0.1);background:rgba(15,33,64,0.5);color:#e0f0f0;font-family:'Sora',sans-serif;font-size:15px;outline:none;transition:all .3s}
.shop-bl-search-wrap input::placeholder{color:rgba(224,240,240,0.2)}
.shop-bl-search-wrap input:focus{border-color:var(--cyan);box-shadow:0 0 0 3px rgba(0,229,204,0.1)}

/* CATEGORIES */
.shop-bl-cats{display:flex;flex-wrap:wrap;justify-content:center;gap:10px;padding:0 24px;margin-bottom:48px}
.shop-bl-cat{padding:8px 20px;border-radius:100px;font-family:'Sora',sans-serif;font-size:13px;font-weight:600;cursor:pointer;border:1px solid rgba(0,229,204,0.1);background:rgba(15,33,64,0.3);color:rgba(224,240,240,0.5);transition:all .3s}
.shop-bl-cat:hover{border-color:rgba(0,229,204,0.2);color:rgba(224,240,240,0.7)}
.shop-bl-cat.active{background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);border-color:transparent;font-weight:700}

/* MSG */
.shop-bl-msg{text-align:center;padding:60px 24px;color:rgba(224,240,240,0.35);font-size:16px}
.shop-bl-msg.error{color:#f87171}

/* GRID */
.shop-bl-grid-section{max-width:1100px;margin:0 auto;padding:0 24px 100px}
.shop-bl-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}

/* CARD */
.shop-bl-card{border-radius:20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);overflow:hidden;transition:all .4s;display:flex;flex-direction:column}
.shop-bl-card:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px);box-shadow:0 20px 60px rgba(0,0,0,0.3)}
.shop-bl-card-img{height:200px;overflow:hidden;position:relative}
.shop-bl-media{width:100%;height:100%;object-fit:cover;transition:transform .5s}
.shop-bl-card:hover .shop-bl-media{transform:scale(1.05)}
.shop-bl-placeholder{height:100%;background:linear-gradient(135deg,rgba(0,229,204,0.06),rgba(34,217,127,0.04));display:flex;align-items:center;justify-content:center;font-size:42px;opacity:0.7}
.shop-bl-card-body{padding:24px;display:flex;flex-direction:column;flex:1}
.shop-bl-card-cat{font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--cyan);margin-bottom:8px}
.shop-bl-card-body h2{font-size:18px;font-weight:700;line-height:1.4;margin-bottom:10px}
.shop-bl-card-body p{font-size:13px;line-height:1.6;color:rgba(224,240,240,0.4);flex:1;margin-bottom:16px}
.shop-bl-card-footer{display:flex;align-items:center;justify-content:space-between}
.shop-bl-card-date{display:flex;align-items:center;gap:6px;font-size:12px;color:rgba(224,240,240,0.3)}
.shop-bl-card-arrow{width:18px;height:18px;color:var(--cyan);display:flex}
.shop-bl-card-arrow svg{width:18px;height:18px}

/* RESPONSIVE */
@media(max-width:900px){
  .shop-bl-hero{padding:48px 24px 36px}
  .shop-bl-grid{grid-template-columns:1fr 1fr}
}
@media(max-width:600px){
  .shop-bl-grid{grid-template-columns:1fr}
}
`;